import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { OAuthProvider } from 'utils/oauth';
import { removeThemeCookie } from 'utils/theme-cookie';

import type { ServerError } from '../../models';
import type {
    CreateSardineSessionResponse,
    GetExchangeStatusResponse,
    GoogleOneTapLoginRequest,
    LoginResponse,
    OautCallbackRequest,
    OAuthApproveRequest,
    OAuthApproveResponse,
    OauthTwoFactorCallbackRequest,
    SessionResponse,
    TwoFactorLoginRequest,
} from '../../models/reqres';
import { exchangeApi, handleErrorCallback, noop, rateLimiter, setLogoutLock } from '../core';

export async function createSardineSession(): Promise<CreateSardineSessionResponse> {
    const response = await exchangeApi.post<CreateSardineSessionResponse>('/sardine/session', {}, { timeout: 3000 });
    return response.data;
}

export function twoFactorLogin(
    payload: TwoFactorLoginRequest,
    onSuc: (resp: LoginResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LoginResponse>('/log_in_mfa?four_digit_code=true', payload, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function oauthLogin(
    provider: OAuthProvider,
    payload: OautCallbackRequest,
    onSuc: (resp: LoginResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LoginResponse>(`/oauth/${provider}/callback?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function oauthTwoFactorLogin(
    payload: OauthTwoFactorCallbackRequest,
    onSuc: (resp: LoginResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LoginResponse>(`/oauth/log_in_mfa_required?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

// Kalshi as OAuth provider: the session approves a third-party client and gets
// back the server-built redirect_url carrying the authorization code.
export function oauthApprove(
    payload: OAuthApproveRequest,
    onSuc: (resp: OAuthApproveResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<OAuthApproveResponse>('/oauth/approve', payload, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function googleOneTapLogin(
    payload: GoogleOneTapLoginRequest,
    onSuc: (resp: LoginResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<LoginResponse>(`/oauth/google_one_tap?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function logout(
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    setLogoutLock(true);
    exchangeApi
        .post('/log_out', {}, { cancelToken: cancelRef.token })
        .then(onSuc)
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            // Clean up
            localStorage.removeItem('kalshi-color-palette');
            removeThemeCookie();

            onFinally();
            rateLimiter.clear(() => {
                setLogoutLock(false);
            });
        });

    return cancelRef;
}

export function startFESession(
    userId: string,
    onSuc: (resp: SessionResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<SessionResponse>(`/users/${userId}/frontend_sessions`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function endFESession(
    userId: string,
    sessionId: string,
    onSuc: (resp: SessionResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete<SessionResponse>(`/users/${userId}/frontend_sessions/${sessionId}`, {
            cancelToken: cancelRef.token,
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getExchangeStatus(
    cached: boolean,
    onSuc: (response: GetExchangeStatusResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetExchangeStatusResponse>(cached ? '/cached/exchange/status' : '/exchange/status', {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deactivateAccount(
    userId: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}`)
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
            rateLimiter.clear(() => {
                setLogoutLock(false);
            });
        });

    return cancelRef;
}
